import type { SubmitEventHandler, FC } from "react";

import { type ReadonlyURLSearchParams, useSearchParams } from "next/navigation";

import type { HttpError } from "@vita-learning/types";

import { Spinner } from "~/shared/components/loading/Spinner";
import { isValidEmail, makeUrl, validateEmail } from "~/shared/utils";

import { PrimaryButton, SocialLogin } from "../../buttons";
import { TextFormGroup, useTextFormGroup } from "../../formgroups";
import { BotInput, useTextInput } from "../../inputs";
import { Link } from "../../interactives";

interface RegisterFormProps {
	readonly error: HttpError | null;
	readonly makeOnSubmit: (
		props: RegisterFormOnSubmitProps
	) => SubmitEventHandler<HTMLFormElement>;
	readonly isLoading: boolean;
	readonly className?: string;
}

export interface RegisterFormOnSubmitProps {
	readonly isEnabled: boolean;
	readonly email: string;
}

const host = process.env.NEXT_PUBLIC_SITE_HOST;

if (!host) throw new Error("NEXT_PUBLIC_SITE_HOST is not defined");

const makeLoginHref = (searchParams: ReadonlyURLSearchParams | null) => {
	const url = makeUrl("/login");

	searchParams?.forEach((v, k) => url.searchParams.set(k, v));

	return url.pathname + url.search;
};

export const RegisterForm: FC<RegisterFormProps> = ({
	error,
	makeOnSubmit,
	isLoading,
	className,
}) => {
	const searchParams = useSearchParams();

	const honeypotInput = useTextInput({});

	const emailInput = useTextFormGroup({
		label: "Email",
		"data-testid": "user-email-error",
		type: "email",
		serverError: error,
		placeholder: "johndoe@example.com",
		validate: validateEmail,
		autoFocus: true,
	});

	const isEnabled = isValidEmail(emailInput.value) && !honeypotInput.value;

	return (
		<>
			<SocialLogin />
			<form
				onSubmit={makeOnSubmit({ email: emailInput.value, isEnabled })}
				className={className}
				data-testid="register-form"
			>
				<TextFormGroup {...emailInput} />
				{honeypotInput && <BotInput {...honeypotInput} />}
				<PrimaryButton
					className={`${className}__button`}
					disabled={!isEnabled}
					type="submit"
					data-testid="create-account-btn"
				>
					{isLoading ? <Spinner color="#FFF" /> : "Create My Account"}
				</PrimaryButton>
				<small style={{ display: "block" }}>
					By clicking <em>Create My Account</em>, you agree to our&nbsp;
					<Link
						className={`${className}__link`}
						href={new URL("/terms", host).href}
						target="_blank"
						rel="noopener noreferrer"
					>
						Terms of Service
					</Link>{" "}
					and&nbsp;
					<Link
						className={`${className}__link`}
						href={new URL("/privacy", host).href}
						target="_blank"
						rel="noopener noreferrer"
					>
						Privacy Policy
					</Link>
					.
				</small>
				<Link
					className={`${className}__link`}
					href={makeLoginHref(searchParams)}
					data-testid="login-link"
				>
					Already have an account? Login
				</Link>
			</form>
		</>
	);
};

export const RegisterFormFallback: FC<Pick<RegisterFormProps, "className">> = ({
	className,
}) => {
	const honeypotInput = useTextInput({});

	const emailInput = useTextFormGroup({
		label: "Email",
		"data-testid": "user-email-error",
		type: "email",
		placeholder: "johndoe@example.com",
		autoFocus: true,
	});

	const fields = { email: emailInput, honeypot: honeypotInput };

	return (
		<>
			<SocialLogin />
			<form className={className} data-testid="register-form">
				<TextFormGroup {...fields.email} />
				{fields.honeypot && <BotInput {...fields.honeypot} />}
				<PrimaryButton
					className={`${className}__button`}
					disabled
					type="submit"
					data-testid="create-account-btn"
				>
					Create My Account
				</PrimaryButton>
				<small style={{ display: "block" }}>
					By clicking <em>Create My Account</em>, you agree to our&nbsp;
					<Link
						className={`${className}__link`}
						href={new URL("/terms", host).href}
						target="_blank"
						rel="noopener noreferrer"
					>
						Terms of Service
					</Link>{" "}
					and&nbsp;
					<Link
						className={`${className}__link`}
						href={new URL("/privacy", host).href}
						target="_blank"
						rel="noopener noreferrer"
					>
						Privacy Policy
					</Link>
					.
				</small>
				<Link
					className={`${className}__link`}
					href="/login"
					data-testid="login-link"
				>
					Already have an account? Login
				</Link>
			</form>
		</>
	);
};
